import { useRef } from 'react'

import { DocumentContext } from '@harvey-frontend/openapi/models/DocumentContext'
import { DraftingInsertRequest } from '@harvey-frontend/openapi/models/DraftingInsertRequest'
import { KnowledgeSourceType } from '@harvey-frontend/openapi/models/KnowledgeSourceType'
import { UploadedFile } from '@harvey-frontend/openapi/models/UploadedFile'
import * as Sentry from '@sentry/react'
import _ from 'lodash'
import { useAbortController } from 'providers/abort-controller-provider'
import { v4 as uuidv4 } from 'uuid'
import { useShallow } from 'zustand/react/shallow'

import useQueryAnalytics from 'components/common/analytics/use-query-analytics'

import { replaceSelection } from '../../../services/document/edit-utilities'
import { fetchDraftingInsertStream } from '../../../services/endpoints'
import { EVENT_KIND } from '../../../utils/constants'
import { useRequestMode } from '../hooks/useRequestMode'
import { useAccessTokenContext } from '../providers/AccessTokenProvider'
import useAppStore from '../stores/store'
import { ensureError } from '../utils/error-utils'
import {
  getOoxmlAroundCursor,
  getTextAroundCursor,
} from '../utils/formatting-utils'
import { useFormatOoxml } from './useFormatOoxml'

export const useFetchInserts = (precedents: UploadedFile[]) => {
  const { userInfo } = useAccessTokenContext()
  const { addAbortController } = useAbortController()
  const { getAccessToken } = useAccessTokenContext()
  const mode = useRequestMode()
  const {
    documentId,
    eventId,
    highlightedText,
    instruction,
    setQueryCompleted,
    setQueryError,
    setQueryId,
  } = useAppStore(
    useShallow((s) => ({
      documentId: s.documentId,
      eventId: s.eventId,
      files: s.files,
      highlightedText: s.highlightedText,
      instruction: s.instruction,
      setQueryCompleted: s.setQueryCompleted,
      setQueryError: s.setQueryError,
      setQueryId: s.setQueryId,
    }))
  )
  const {
    recordQuerySubmitted,
    recordQueryCancel,
    recordQueryCompletion,
    recordQueryError,
  } = useQueryAnalytics(EVENT_KIND)
  const { mutateAsync: formatOoxml, reset: resetFormatOoxml } = useFormatOoxml()

  const selectionRef = useRef<Word.Range | null>(null)

  const initializeSelection = async () => {
    return Word.run(async (context) => {
      const selection = context.document.getSelection()
      selection.load('text')
      selection.track()
      await context.sync()
      selectionRef.current = selection
    })
  }

  const applyInsertCallback = async (newText: string) => {
    // The response frequently includes leading newlines.
    // For now, we can trim those out. Ideally, we should not send these
    // at all.
    newText = newText.replace(/^\n+/, '')
    if (selectionRef.current) {
      await replaceSelection(newText, selectionRef.current)
    }
  }

  return async () => {
    const queryId = uuidv4()
    setQueryId(queryId)
    try {
      const accessToken = await getAccessToken()
      if (!accessToken) {
        return
      }
      setQueryError(null)
      setQueryCompleted(false)

      await initializeSelection()
      const abortController = new AbortController()
      addAbortController(mode, abortController)
      const { textBefore, textAfter, textInParagraph } =
        await getTextAroundCursor()
      // Grab the surrounding OOXML up here so that the initial insert doesn't mess it up.
      const ooxmlAroundCursor = await getOoxmlAroundCursor()
      const request: DraftingInsertRequest = {
        instructions: instruction,
        wordDocumentId: documentId,
        eventId,
        documentContext: {
          textBefore,
          textAfter,
        } as DocumentContext,
      }

      if (precedents.length > 0) {
        request.knowledgeSources = [
          {
            type: KnowledgeSourceType.FILES,
            fileIds: precedents.map((precedent) => precedent.id),
          },
        ]
      }

      recordQuerySubmitted({
        type: mode,
        query_id: queryId,
        clause_length: highlightedText?.text.length || 0,
        instruction_length: instruction.length,
        word_document_id: documentId,
      })
      const result = await fetchDraftingInsertStream({
        accessToken,
        abortController,
        request,
        updateCallback: async (response) => {
          if (!_.isEmpty(response)) {
            await applyInsertCallback(response)
          }
        },
      })

      if (_.isEmpty(result) || _.every(result, _.isNil)) {
        throw new Error(
          'Failed to generate new text to insert for the given query.'
        )
      }

      if (userInfo?.IsInternalUser) {
        // Make a call to formatOoxml to get the OOXML to insert.
        resetFormatOoxml()
        const inParagraph = textInParagraph.length > 0
        await formatOoxml({
          instructions: instruction,
          wordDocumentId: documentId,
          textToInsert: result.at(-1) || '',
          ooxmlAfter: ooxmlAroundCursor.after,
          ooxmlBefore: ooxmlAroundCursor.before,
          inParagraph,
        })
      }
      recordQueryCompletion({
        type: mode,
        query_id: queryId,
        insert_length: result.length,
      })
      setQueryCompleted(true)
    } catch (e) {
      const error = ensureError(e)
      console.error(error)
      if (error.name === 'AbortError') {
        setQueryError({ reason: 'Request was canceled.' })
        recordQueryCancel({
          type: mode,
          query_id: queryId,
          clause_length: highlightedText?.text.length || 0,
          instruction_length: instruction.length,
          error_message: error.message,
          error_name: error.name,
          error_stack: error.stack ?? '',
        })
      } else {
        Sentry.captureException(error)
        setQueryError({ reason: error.message })
        recordQueryError({
          type: mode,
          clause_length: highlightedText?.text.length || 0,
          instruction_length: instruction.length,
          error_message: error.message,
          error_name: error.name,
          error_stack: error.stack ?? '',
          query_id: queryId,
        })
      }
    }
  }
}
